import type { LightUUID, Scene, SceneLight } from "../types/scene";
import { generateUuid } from "../utils/id";
import {
  DEFAULT_COMPILER_SETTINGS,
  DEFAULT_STATISTIC_SETTINGS,
  normalizeCompilerSettings,
  normalizeStatisticSettings,
} from "./mjcfSettingsStore";

export const DEFAULT_SCENE_OPTION: Scene["option"] = {
  timestep: 0.002,
  gravity: [0, 0, -9.81],
  wind: [0, 0, 0],
  integrator: "implicitfast",
  iterations: 100,
  tolerance: 1e-8,
  solver: "Newton",
  collision: "dynamic",
  cone: "pyramidal",
  jacobian: "auto",
  impratio: 1,
  noslip_iterations: 0,
  noslip_tolerance: 1e-6,
  mpr_iterations: 50,
  mpr_tolerance: 1e-6,
  density: 0,
  viscosity: 0,
  o_margin: 0,
  o_solref: [0.02, 1],
  o_solimp: [0.9, 0.95, 0.001, 0.5, 2],
  flags: {},
};

export const DEFAULT_SCENE_LIGHTS: Record<LightUUID, SceneLight> = {
  ["default-main-light" as LightUUID]: {
    uuid: "default-main-light" as LightUUID,
    name: "main_light",
    kind: "fixed",
    position: [2, 4, 5],
    direction: [-2, -4, -5],
    type: "directional",
    active: true,
    castshadow: true,
    diffuse: [0.98, 0.98, 0.98],
    ambient: [0, 0, 0],
    specular: [0.3, 0.3, 0.3],
    range: 10,
    cutoff: 45,
    exponent: 10,
    attenuation: [1, 0, 0],
    intensity: 0.7,
  },
  ["default-fill-light" as LightUUID]: {
    uuid: "default-fill-light" as LightUUID,
    name: "fill_light",
    kind: "fixed",
    position: [-2, 3, 3],
    direction: [2, -3, -3],
    type: "directional",
    active: true,
    castshadow: true,
    diffuse: [0.96, 0.96, 0.96],
    ambient: [0, 0, 0],
    specular: [0.3, 0.3, 0.3],
    range: 10,
    cutoff: 45,
    exponent: 10,
    attenuation: [1, 0, 0],
    intensity: 0.4,
  },
  ["default-overhead-light" as LightUUID]: {
    uuid: "default-overhead-light" as LightUUID,
    name: "overhead_light",
    kind: "fixed",
    position: [0, 5, 0],
    direction: [0, -1, 0],
    type: "directional",
    active: true,
    castshadow: true,
    diffuse: [0.9, 0.95, 1],
    ambient: [0, 0, 0],
    specular: [0.3, 0.3, 0.3],
    range: 10,
    cutoff: 45,
    exponent: 10,
    attenuation: [1, 0, 0],
    intensity: 0.2,
  },
};

const createDefaultSceneLights = (): Record<LightUUID, SceneLight> =>
  Object.fromEntries(
    Object.entries(DEFAULT_SCENE_LIGHTS).map(([uuid, light]) => [
      uuid,
      {
        ...light,
        position: [...light.position],
        direction: [...light.direction],
        diffuse: [...light.diffuse],
        ambient: [...light.ambient],
        specular: [...light.specular],
        attenuation: [...light.attenuation],
      },
    ])
  ) as Record<LightUUID, SceneLight>;

export const createEmptyScene = (): Scene => ({
  uuid: generateUuid(),
  name: "Untitled Scene",
  mjcfCompiler: normalizeCompilerSettings(DEFAULT_COMPILER_SETTINGS),
  option: { ...DEFAULT_SCENE_OPTION, gravity: [...DEFAULT_SCENE_OPTION.gravity] },
  mjcfStatistic: normalizeStatisticSettings(DEFAULT_STATISTIC_SETTINGS),
  bodies: {},
  worldBodies: [], // WorldBody[]
  joints: {},
  sites: {},
  cameras: {},
  lights: createDefaultSceneLights(),
  background: { type: "builtin" },
  backgroundLibrary: {},
  materials: {},
  textures: {},
  meshes: {},
  hfields: {},
  tendons: {},
  actuators: {},
  sensors: {},
  contactPairs: [],
  contactExcludes: [],
  equality: {},
});
